import { LitElement, html, nothing, type PropertyValues, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { label, type ShopFilterGroup } from './shop-types.ts';

type Toggle = 'all' | 'none' | null;

/**
 * Eine Filtergruppe (z. B. "Regelwerk") als aufklappbarer Kasten mit Kontrollkästchen
 * und Trefferzahl, darunter "alle"/"keine" wie im alten Shop. Die Auswahl hält
 * <svgw-shop>; die Gruppe meldet nur Änderungen:
 *
 * - shop-toggle: Kasten auf-/zuklappen
 * - shop-change: neue Auswahl (detail: string[])
 * - shop-apply:  Auswahl übernehmen und suchen
 *
 * "alle"/"keine" sind Aktionen wie im alten Shop: angehakt nur nach eigenem Klick,
 * ein einzelnes Kästchen hebt sie wieder auf. Sie wirken nur auf die eigene Gruppe -
 * im alten Shop setzten die Selektoren alle Gruppen der Seite zurück.
 *
 * Die Sprache darf hier leer sein; ohne Auswahl setzt der Server beim Übernehmen
 * die Sprache der Seite ein.
 */
@customElement('svgw-shop-filter-group')
export class SvgwShopFilterGroup extends LitElement {
    @property({ type: Object }) group: ShopFilterGroup | null = null;
    @property({ type: Array }) selected: string[] = [];
    @property({ type: Boolean }) open = false;

    @state() private toggle: Toggle = null;

    protected createRenderRoot(): HTMLElement {
        return this;
    }

    protected willUpdate(changed: PropertyValues<this>): void {
        // Neue Daten vom Server (übernommen) oder Kasten zugeklappt: Aktionen zurücksetzen.
        if (changed.has('group') || (changed.has('open') && !this.open)) {
            this.toggle = null;
        }
    }

    private emit(name: string, detail?: unknown): void {
        this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
    }

    private toggleItem(input: HTMLInputElement, id: string): void {
        this.toggle = null;
        this.emit('shop-change', input.checked
            ? [...new Set([...this.selected, id])]
            : this.selected.filter((x) => x !== id));
    }

    /** Wie alt: "alle" setzt alles, "alle" wegnehmen leert die Gruppe, "keine" leert sie. */
    private toggleAll(input: HTMLInputElement): void {
        this.toggle = input.checked ? 'all' : null;
        this.emit('shop-change', input.checked ? this.group?.items.map((i) => i.id) ?? [] : []);
    }

    private toggleNone(input: HTMLInputElement): void {
        this.toggle = input.checked ? 'none' : null;
        if (input.checked) {
            this.emit('shop-change', []);
        }
    }

    protected render(): TemplateResult | typeof nothing {
        const group = this.group;
        if (group === null || group.items.length === 0) {
            return nothing;
        }

        const titleClasses = { 'shop-filter__title': true, active: this.open, enabled: this.selected.length > 0 };
        const configId = `shop-filter-${group.key}`;

        return html`
            <section class="shop-filter">
                <button type="button" class=${classMap(titleClasses)} aria-expanded=${this.open} aria-controls=${configId}
                        @click=${() => this.emit('shop-toggle')}>
                    ${group.label}
                    <i class="fa ${this.open ? 'fa-chevron-up' : 'fa-chevron-down'}"></i>
                </button>
                <div id=${configId} class="shop-filter__config" ?hidden=${!this.open}>
                    <div class="shop-filter__list">
                        ${group.items.map((item) => {
                            const id = `${group.key}-${item.id}`;
                            return html`
                                <div class="form-check">
                                    <input class="form-check-input" type="checkbox" id=${id} name=${group.key} value=${item.id}
                                           .checked=${this.selected.includes(item.id)}
                                           @change=${(e: Event) => this.toggleItem(e.target as HTMLInputElement, item.id)}>
                                    <label class="form-check-label small" for=${id}>
                                        <span>${item.label}</span>
                                        <span class="shop-filter__hits">${item.count}</span>
                                    </label>
                                </div>`;
                        })}
                    </div>
                    <hr>
                    <div class="shop-filter__toggles">
                        <div class="form-check form-check-inline">
                            <input class="form-check-input" type="checkbox" id="${configId}-alle" .checked=${this.toggle === 'all'}
                                   @change=${(e: Event) => this.toggleAll(e.target as HTMLInputElement)}>
                            <label class="form-check-label small" for="${configId}-alle">${label('Shop.All')}</label>
                        </div>
                        <div class="form-check form-check-inline">
                            <input class="form-check-input" type="checkbox" id="${configId}-keine" .checked=${this.toggle === 'none'}
                                   @change=${(e: Event) => this.toggleNone(e.target as HTMLInputElement)}>
                            <label class="form-check-label small" for="${configId}-keine">${label('Shop.None')}</label>
                        </div>
                    </div>
                    <button type="button" class="btn btn-primary btn-sm mt-2" @click=${() => this.emit('shop-apply')}>
                        ${label('Shop.Apply')}
                    </button>
                </div>
            </section>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'svgw-shop-filter-group': SvgwShopFilterGroup;
    }
}
